import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { ShoppingBag, User, LogOut, Loader2, Plus, ClipboardList, ArrowLeft, Package, ChevronRight } from "lucide-react";
import { TicketDialog, TicketList } from "./SupportFAB";

export default function ShopAccount() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", pin_code: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [ticketOpen, setTicketOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [meRes, ticRes, ordRes] = await Promise.all([
        api.get("/store/me"),
        api.get("/store/tickets").catch(() => ({ data: [] })),
        api.get("/store/orders").catch(() => ({ data: [] })),
      ]);
      setMe(meRes.data);
      setForm({
        name: meRes.data.name || "",
        address: meRes.data.address || "",
        pin_code: meRes.data.pin_code || "",
      });
      setTickets(ticRes.data || []);
      setOrders(ordRes.data || []);
    } catch {
      nav("/shop/login");
    } finally {
      setLoading(false);
      setTicketsLoading(false);
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/store/me", form);
      toast.success("Profile updated");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    nav("/shop/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <Link
          to="/shop"
          className="inline-flex items-center gap-1.5 bg-[var(--shop-primary,#0055FF)] text-white hover:bg-[var(--shop-primary,#0040CC)] font-semibold text-sm px-3 py-1.5 rounded-md shadow-sm transition-transform hover:-translate-x-0.5"
          data-testid="shop-account-back-to-shop"
        >
          <ArrowLeft className="w-4 h-4 stroke-[3]" />
          Shop
        </Link>
        <Link to="/shop" className="flex items-center gap-2 hover:opacity-80" data-testid="shop-account-logo">
          <ShoppingBag className="w-5 h-5 text-[var(--shop-primary,#0055FF)]" />
          <span className="font-heading font-bold">DialFlow Shop</span>
        </Link>
        <button
          onClick={logout}
          className="text-slate-500 hover:text-red-600 flex items-center gap-1 text-sm"
          data-testid="shop-logout-btn"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </header>

      <div className="max-w-2xl mx-auto p-4 md:p-8">
        <div className="mb-6">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF5000]">Account</div>
          <h1 className="font-heading text-2xl font-bold mt-1 flex items-center gap-2">
            <User className="w-6 h-6" /> Hello, {me?.name}
          </h1>
          <div className="mono text-xs text-slate-500 mt-1">{me?.mobile}</div>
        </div>

        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-5 space-y-3">
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="shop-account-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Delivery address</Label>
              <Textarea
                rows={3}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="shop-account-address"
              />
            </div>
            <div className="space-y-2">
              <Label>PIN code</Label>
              <Input
                maxLength={6}
                inputMode="numeric"
                value={form.pin_code}
                onChange={(e) => setForm({ ...form, pin_code: e.target.value })}
                data-testid="shop-account-pin"
              />
            </div>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)]"
              data-testid="shop-account-save"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save changes
            </Button>
          </CardContent>
        </Card>

        {/* Order history — quick tap-through to the full list of past orders,
            with a live count so the customer knows what's there. */}
        <Card className="border-slate-200 shadow-none mt-6" data-testid="shop-order-history-card">
          <Link
            to="/shop/orders"
            className="block hover:bg-slate-50 rounded-lg transition-colors"
            data-testid="shop-order-history-link"
          >
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-[var(--shop-primary,#0055FF)]/10 flex items-center justify-center shrink-0">
                <Package className="w-5 h-5 text-[var(--shop-primary,#0055FF)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-lg">Order history</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {orders.length === 0
                    ? "You haven't placed any orders yet"
                    : `${orders.length} order${orders.length === 1 ? "" : "s"} — tap to view details, track, or reorder`}
                </div>
              </div>
              {orders.length > 0 && (
                <span
                  className="mono text-[10px] px-2 py-0.5 rounded uppercase tracking-widest bg-[var(--shop-primary,#0055FF)]/10 text-[var(--shop-primary,#0055FF)] font-semibold"
                  data-testid="shop-order-history-count"
                >
                  {orders.length}
                </span>
              )}
              <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
            </CardContent>
          </Link>
        </Card>

        {/* Service tickets — customer raises them here; owner sees them as tasks. */}
        <Card className="border-slate-200 shadow-none mt-6" data-testid="shop-tickets-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-[var(--shop-primary,#0055FF)]" />
                <h2 className="font-heading font-semibold text-lg">My service tickets</h2>
                {tickets.length > 0 && (
                  <span className="mono text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">
                    {tickets.length}
                  </span>
                )}
              </div>
              <Button
                size="sm"
                onClick={() => setTicketOpen(true)}
                className="bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)] h-8"
                data-testid="shop-open-ticket-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Raise a ticket
              </Button>
            </div>
            <TicketList tickets={tickets} loading={ticketsLoading} />
          </CardContent>
        </Card>
      </div>

      <TicketDialog
        open={ticketOpen}
        onOpenChange={setTicketOpen}
        orders={orders}
        loginMobile={me?.mobile || ""}
        onCreated={load}
      />
    </div>
  );
}
